import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  CreditPayment,
  PaymentWorkflowResponse,
} from '../models/invoice/invoice.model';
import {
  SourceComponent,
  TransactionSource,
} from '../models/invoice/payment.model';
import { NewSendReceipt, SendReceipt } from '../models/invoice/cash-payment.model';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
  }),
};

const httpOptionsDownload = {
  headers: {
    'Content-Type': 'application/json',
  },
  params: null,
  reportProgress: true,
  responseType: 'arraybuffer',
  withCredentials: false,
};

@Injectable({
  providedIn: 'root',
})
export class CreditPaymentService {
  private baseRoute: string;

  constructor(private httpClient: HttpClient) {
    this.baseRoute = environment.paymentAPIRoot;
  }

  creditCharge<T>(
    payload: CreditPayment,
    userId: string,
    locationId: string,
    branchName: string
  ): Observable<T> {
    payload.TransactionSource = TransactionSource.Merchant;
    return this.httpClient.post<T>(
      `${this.baseRoute}/api/credit/charge?userId=${userId}&locationId=${locationId}&branchName=${branchName}`,
      payload,
      httpOptions
    );
  }

  creditChargeForm<T>(
    payload: CreditPayment,
    userId: string,
    locationId: string,
    branchName: string
  ): Observable<T> {
    payload.TransactionSource = SourceComponent.PaymentForms;
    return this.httpClient.post<T>(
      `${this.baseRoute}/api/credit/charge?userId=${userId}&locationId=${locationId}&branchName=${branchName}`,
      payload,
      httpOptions
    );
  }

  creditAuthorize<T>(payload: CreditPayment): Observable<T> {
    payload.TransactionSource = TransactionSource.Merchant;
    return this.httpClient.post<T>(
      `${this.baseRoute}/api/credit/authorize`,
      payload,
      httpOptions
    );
  }

  creditVerify<T>(payload: CreditPayment): Observable<T> {
    return this.httpClient.post<T>(
      `${this.baseRoute}/api/credit/verify`,
      payload,
      httpOptions
    );
  }

  creditVerifyForm<T>(payload: CreditPayment): Observable<T> {
    return this.httpClient.post<T>(
      `${this.baseRoute}/api/credit/verify`,
      payload,
      httpOptions
    );
  }

  creditBalanceInquiry<T>(payload: CreditPayment): Observable<T> {
    payload.TransactionSource = TransactionSource.Merchant;
    return this.httpClient.post<T>(
      `${this.baseRoute}/api/credit/balanceinquiry`,
      payload,
      httpOptions
    );
  }

  creditRefund<T>(payload: CreditPayment): Observable<T> {
    payload.TransactionSource = TransactionSource.Merchant;
    return this.httpClient.post<T>(
      `${this.baseRoute}/api/credit/refund`,
      payload,
      httpOptions
    );
  }

  creditReverse<T>(payload: CreditPayment): Observable<T> {
    payload.TransactionSource = TransactionSource.Merchant;
    return this.httpClient.post<T>(
      `${this.baseRoute}/api/credit/reverse`,
      payload,
      httpOptions
    );
  }

  creditVoid<T>(payload: CreditPayment): Observable<T> {
    payload.TransactionSource = TransactionSource.Merchant;
    return this.httpClient.post<T>(
      `${this.baseRoute}/api/credit/void`,
      payload,
      httpOptions
    );
  }

  getCardType<T>(cardNumber: string): Observable<T> {
    return this.httpClient.get<T>(`https://lookup.binlist.net/${cardNumber}`);
  }

  txncreditAuthorize<T>(
    payload: CreditPayment,
    isUserZero: boolean
  ): Observable<T> {
    payload.TransactionSource = TransactionSource.Merchant;
    return this.httpClient.post<T>(
      `${this.baseRoute}/api/credittransaction/authorize?isUserZero=${isUserZero}`,
      payload,
      httpOptions
    );
  }

  txncreditVerify<T>(
    payload: CreditPayment,
    isUserZero: boolean
  ): Observable<T> {
    payload.TransactionSource = TransactionSource.Merchant;
    return this.httpClient.post<T>(
      `${this.baseRoute}/api/credittransaction/verify?isUserZero=${isUserZero}`,
      payload,
      httpOptions
    );
  }

  txncreditBalanceInquiry<T>(
    payload: CreditPayment,
    isUserZero: boolean
  ): Observable<T> {
    payload.TransactionSource = TransactionSource.Merchant;
    return this.httpClient.post<T>(
      `${this.baseRoute}/api/credittransaction/balanceinquiry?isUserZero=${isUserZero}`,
      payload,
      httpOptions
    );
  }

  txncreditRefund<T>(
    payload: CreditPayment,
    isUserZero: boolean
  ): Observable<T> {
    payload.TransactionSource = TransactionSource.Merchant;
    return this.httpClient.post<T>(
      `${this.baseRoute}/api/credittransaction/refund?isUserZero=${isUserZero}`,
      payload,
      httpOptions
    );
  }

  txncreditReverse<T>(
    payload: CreditPayment,
    isUserZero: boolean
  ): Observable<T> {
    payload.TransactionSource = TransactionSource.Merchant;
    return this.httpClient.post<T>(
      `${this.baseRoute}/api/credittransaction/reverse?isUserZero=${isUserZero}`,
      payload,
      httpOptions
    );
  }

  txncreditVoid<T>(payload: CreditPayment, isUserZero: boolean): Observable<T> {
    payload.TransactionSource = TransactionSource.Merchant;
    return this.httpClient.post<T>(
      `${this.baseRoute}/api/credittransaction/void?isUserZero=${isUserZero}`,
      payload,
      httpOptions
    );
  }

  creditChargeOnly<T>(
    payload: CreditPayment,
    userId: string,
    locationId: string,
    branchName: string
  ): Observable<T> {
    payload.TransactionSource = TransactionSource.Merchant;
    return this.httpClient.post<T>(
      `${this.baseRoute}/api/credit/chargeonly?userId=${userId}&locationId=${locationId}&branchName=${branchName}`,
      payload,
      httpOptions
    );
  }

  addAuditTrail<T>(payload: PaymentWorkflowResponse): Observable<T> {
    return this.httpClient.post<T>(
      `${this.baseRoute}/api/credit/addaudittrail`,
      payload,
      httpOptions
    );
  }

  sendReceipt<T>(payload: PaymentWorkflowResponse): Observable<T> {
    return this.httpClient.post<T>(
      `${this.baseRoute}/api/credit/sendreceipt`,
      payload,
      httpOptions
    );
  }

  processTip<T>(payload: PaymentWorkflowResponse): Observable<T> {
    return this.httpClient.post<T>(
      `${this.baseRoute}/api/credit/processtip`,
      payload,
      httpOptions
    );
  }

  getCashPaymentByInvoiceNumber<T>(invoiceNumber: string): Observable<T> {
    return this.httpClient.get<T>(
      `${this.baseRoute}/api/CashPayment/invoice/${invoiceNumber}`
    );
  }
  sendFormReceipt<T>(payload: SendReceipt): Observable<T> {
    return this.httpClient.post<T>(
      `${this.baseRoute}/api/CashPayment/receipt/send`,
      payload,
      httpOptions
    );
  }

  newSendFormReceipt<T>(payload: NewSendReceipt): Observable<string> {
    return this.httpClient.post<string>(
      // CashPayment/receipt/send
      `${this.baseRoute}/api/Receipt/sendreceipt`,
      payload,
      { responseType: 'text' as 'json' }
    );
  }

  generateManifest<T>(): Observable<T> {
    return this.httpClient.get<T>(
      `${this.baseRoute}/api/credit/generatemanifest`
    );
  }
}
